import React, { useState } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Select, 
  Space, 
  Typography,
  Progress,
  Tag,
  Button,
  Tooltip,
  Empty
} from 'antd';
import { 
  ProjectOutlined, 
  CalendarOutlined,
  DollarOutlined,
  TeamOutlined 
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';

const { Option } = Select;
const { Text } = Typography;

const ProjectProgress = () => {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState('');

  // 获取项目列表
  const { data: projectsData } = useQuery(
    ['projectsProgress', typeFilter],
    () => axios.get('/projects', {
      params: {
        type: typeFilter || undefined,
        limit: 100
      }
    }).then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  // 兜底用：获取用户列表用于根据ID映射姓名
  const { data: allUsersData } = useQuery(
    'allUsersForProgress',
    () => axios.get('/users').then(res => res.data),
    { staleTime: 5 * 60 * 1000 }
  );

  // 根据ID获取真实姓名（兜底）
  const getUserRealNameById = (userId) => {
    if (!userId) return '';
    const users = allUsersData?.data?.users || [];
    const u = users.find(u => u.id === userId || u.id === Number(userId));
    return u?.realName || u?.username || '';
  };



  const getTypeText = (type) => {
    const texts = {
      bidding: '投标',
      application: '申报',
      deepening: '深化',
      consultation: '咨询',
      other: '其他'
    };
    return texts[type] || type;
  };

  const isOverdue = (expectedDate) => {
    return dayjs().isAfter(dayjs(expectedDate));
  };

  const getRiskLevel = (project) => {
    const daysToDeadline = dayjs(project.expectedDeliveryDate).diff(dayjs(), 'day');
    const progressGap = 100 - project.progress;
    
    if (isOverdue(project.expectedDeliveryDate)) {
      return { level: 'high', text: '已逾期', color: 'red' };
    }
    
    if (daysToDeadline <= 3 && progressGap > 20) {
      return { level: 'high', text: '高风险', color: 'red' };
    }
    
    if (daysToDeadline <= 7 && progressGap > 30) {
      return { level: 'medium', text: '中风险', color: 'orange' };
    }
    
    return { level: 'low', text: '正常', color: 'green' };
  };



  const ProjectCard = ({ project }) => {
    const risk = getRiskLevel(project);
    const leaderName = project?.designLeader?.realName || getUserRealNameById(project?.designLeaderId);
    
    return (
      <Card 
        size="small" 
        className="kanban-card"
        style={{ 
          marginBottom: '12px',
          borderLeft: '4px solid #1890ff'
        }}
        onClick={() => navigate(`/projects/${project.id}`)}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div>
            <Text strong>{project.name}</Text>
            <div style={{ float: 'right' }}>
              <Tag color={risk.color} size="small">
                {risk.text}
              </Tag>
            </div>
          </div>
          
          <Tag size="small">{getTypeText(project.type)}</Tag>
          
          <Progress 
            percent={project.progress} 
            size="small"
            status={project.progress === 100 ? 'success' : 'active'}
          />
          
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <CalendarOutlined /> {dayjs(project.expectedDeliveryDate).format('MM-DD')}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <DollarOutlined /> ¥{(project.budget / 10000).toFixed(1)}万
            </Text>
          </div>
          
          {leaderName && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <TeamOutlined /> {leaderName}
            </Text>
          )}
        </Space>
      </Card>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title={
          <Space>
            <ProjectOutlined />
            项目进度地图
          </Space>
        }
        className="status-card"
      >
        {/* 筛选条件 */}
        <div style={{ marginBottom: '24px' }}>
          <Space wrap>
            <Select
              placeholder="项目类型"
              allowClear
              style={{ width: 120 }}
              onChange={setTypeFilter}
            >
              <Option value="bidding">投标</Option>
              <Option value="application">申报</Option>
              <Option value="deepening">深化</Option>
              <Option value="consultation">咨询</Option>
              <Option value="other">其他</Option>
            </Select>
          </Space>
        </div>

        {/* 项目列表视图 */}
        <Row gutter={[16, 16]}>
          {projectsData?.projects?.length === 0 ? (
            <Col span={24}>
              <Empty 
                description="暂无项目"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ margin: '40px 0' }}
              />
            </Col>
          ) : (
            projectsData?.projects?.map(project => (
              <Col xs={24} sm={12} lg={8} key={project.id}>
                <ProjectCard project={project} />
              </Col>
            ))
          )}
        </Row>

        {/* 风险提示 */}
        <Card 
          title="风险预警" 
          size="small" 
          style={{ marginTop: '24px' }}
        >
          <Row gutter={[16, 16]}>
            {projectsData?.projects?.filter(p => {
              const risk = getRiskLevel(p);
              return risk.level === 'high';
            }).map(project => (
              <Col xs={24} sm={12} lg={8} key={project.id}>
                <Card 
                  size="small"
                  style={{ borderLeft: '4px solid #ff4d4f' }}
                >
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text strong>{project.name}</Text>
                      <Tag color="red">高风险</Tag>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <CalendarOutlined /> {dayjs(project.expectedDeliveryDate).format('YYYY-MM-DD')}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        <DollarOutlined /> ¥{(project.budget / 10000).toFixed(1)}万
                      </Text>
                    </div>
                    { (project?.designLeader?.realName || getUserRealNameById(project?.designLeaderId)) && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        负责人：{project?.designLeader?.realName || getUserRealNameById(project?.designLeaderId)}
                      </Text>
                    )}
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      </Card>
    </div>
  );
};

export default ProjectProgress;
